// MatchTimeline — Iter 32.
// Horizontally scrollable chip strip showing each finish in a match with
// the running score at that point. Referees click any chip (or the start
// pill) to rewind atomically via POST /api/matches/{id}/rewind_to.
import { api, formatApiError } from "../lib/api";
import { t } from "../lib/i18n";
import { toast } from "sonner";

export default function MatchTimeline({ match, playerA, playerB, finishTypes, busy, onRewind }) {
  const finishes = match.finishes || [];
  const rewind = async (index) => {
    if (busy) return;
    try {
      await api.post(`/matches/${match.id}/rewind_to`, { index });
      await onRewind();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.failed);
    }
  };

  // Running score after each finish is applied. index 0 = state before any
  // finish, index N (last) = final live state.
  let sa = 0, sb = 0;
  const rows = finishes.map((f) => {
    const pts = Number(f.points || 0);
    const isA = f.player_id === match.player_a_id;
    if (isA) sa += pts; else sb += pts;
    const label = finishTypes?.[f.finish_type]?.label || f.finish_type;
    return { ...f, sa, sb, isA, label, pts };
  });

  return (
    <div data-testid="match-timeline" className="border border-white/10 bg-[#0C0C14]/60 p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#00E5FF] font-mono">
          // {t.timelineTitle}
        </span>
        <span className="text-[10px] font-mono text-white/40">
          {finishes.length} {t.timelineTurns}
        </span>
      </div>
      <div className="flex items-stretch gap-1.5 overflow-x-auto pb-1">
        {/* Start pill — rewinds ALL finishes (score 0-0) */}
        <button
          onClick={() => rewind(0)}
          disabled={busy || finishes.length === 0}
          data-testid="timeline-rewind-start"
          title={t.timelineRewindStart}
          className="shrink-0 px-2 py-2 border border-white/15 text-white/60 hover:border-[#FF0055]/50 hover:text-[#FF0055] disabled:opacity-30 disabled:cursor-not-allowed text-[10px] font-mono uppercase tracking-[0.15em]"
        >
          ⏮ 0-0
        </button>
        {rows.length === 0 && (
          <div className="text-[10px] font-mono text-white/30 self-center px-2">
            {t.timelineEmpty}
          </div>
        )}
        {rows.map((r, idx) => {
          const p = r.isA ? playerA : playerB;
          const accent = r.isA ? "#00E5FF" : "#FF0055";
          return (
            <button
              key={idx}
              onClick={() => rewind(idx)}
              disabled={busy}
              data-testid={`timeline-chip-${idx}`}
              title={t.timelineRewindTo?.replace("{n}", String(idx))}
              className="shrink-0 flex flex-col items-start gap-0.5 px-2 py-1.5 border rounded-sm text-left transition-colors disabled:cursor-not-allowed"
              style={{
                borderColor: `${accent}66`,
                background: `${accent}0d`,
              }}
              onMouseEnter={(e) => { if (!busy) e.currentTarget.style.background = `${accent}22`; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = `${accent}0d`; }}
            >
              <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.15em]" style={{ color: accent }}>
                <span>{idx + 1}</span>
                <span className="text-white/40">·</span>
                <span className="truncate max-w-[70px]">{p?.handle || "?"}</span>
              </div>
              <div className="text-[10px] font-bold text-white/90">
                {r.label} <span className="text-white/50">+{r.pts}</span>
              </div>
              <div className="text-[9px] font-mono tabular-nums text-white/60">
                {r.sa}-{r.sb}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
